import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DeviceConfigService } from '@shared/services/config/device-config.service';
import { DeviceConfig } from '@shared/models/device-config';
import { WorkingStatusEnum } from '@shared/models/working-status-enum';
import { FonctionsService } from '@shared/services/utilities/fonctions.service';
import { CurrentStateService } from 'app/core/services/current-state.service';

@Component({
  selector: 'app-device-config',
  imports: [TranslateModule, ReactiveFormsModule, FormsModule, CommonModule],
  templateUrl: './device-config.component.html',
  styleUrl: './device-config.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DeviceConfigComponent {
  @Input() deviceSerialNumber: string | null = null;
  @Input() siteId: number;
  @Input() formGroup: FormGroup;
  statusOptions = [      { value: WorkingStatusEnum.NO_REC, label: 'DEVICE_CONFIG.NO_RECORDING' },
      { value: WorkingStatusEnum.WAVE, label: 'DEVICE_CONFIG.WAVE_MODE' },
      { value: WorkingStatusEnum.BAR, label: 'DEVICE_CONFIG.BAR_MODE' },
      { value: WorkingStatusEnum.BOTH, label: 'DEVICE_CONFIG.BOTH_MODE' }
    ];
  readonly recTimeOptions = [1, 2, 4, 8, 16, 32];

  readonly days = [
    { key: 'DEVICE_CONFIG.MON', bit: 0 },
    { key: 'DEVICE_CONFIG.TUE', bit: 1 },
    { key: 'DEVICE_CONFIG.WED', bit: 2 },
    { key: 'DEVICE_CONFIG.THU', bit: 3 },
    { key: 'DEVICE_CONFIG.FRI', bit: 4 },
    { key: 'DEVICE_CONFIG.SAT', bit: 5 },
    { key: 'DEVICE_CONFIG.SUN', bit: 6 },
  ];

  isDayActive(bit: number): boolean {
    const value: number = this.formGroup.get('activeDays')?.value ?? 0;
    return (value & (1 << bit)) !== 0;
  }

  toggleDay(bit: number): void {
    const ctrl = this.formGroup.get('activeDays');
    if (!ctrl) return;
    const current: number = ctrl.value ?? 0;
    const updated = current ^ (1 << bit);
    ctrl.setValue(updated);
    ctrl.markAsDirty();
    this.formGroup.markAsDirty();
    this.formGroup.markAsTouched();
  }

  stepField(controlName: string, direction: 1 | -1, min: number, max: number, step: number = 1): void {
    const ctrl = this.formGroup.get(controlName);
    if (!ctrl) return;
    const current = Number(ctrl.value) || 0;
    const decimals = step < 1 ? String(step).split('.')[1]?.length ?? 0 : 0;
    const newValue = parseFloat(Math.min(max, Math.max(min, current + direction * step)).toFixed(decimals));
    ctrl.setValue(newValue);
    ctrl.markAsDirty();
    this.formGroup.markAsDirty();
    this.formGroup.markAsTouched();
  }

//   WorkingStatusEnum = WorkingStatusEnum;

  constructor(
    private deviceConfigService: DeviceConfigService,
    private currentStateService: CurrentStateService,
    private fonctionsService: FonctionsService,
    private translate: TranslateService
  ) {}

  async submitDeviceConfigForm() {
    if (this.formGroup.untouched && !this.formGroup.dirty) {
      return;
    }

    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      this.fonctionsService.launch_toast(false, this.translate.instant('DEVICE_CONFIG.FORM_INVALID'));
      return;
    }

    const formValue = this.formGroup.getRawValue();
    const recModeNum = Number(formValue.recMode);
    const BARGRAPH_MODES = [WorkingStatusEnum.BAR, WorkingStatusEnum.BOTH];
    const hasBargraphMode = BARGRAPH_MODES.includes(recModeNum);
    if (formValue.realTUse && !hasBargraphMode) {
      this.fonctionsService.launch_toast(
        false,
        this.translate.instant('DEVICE_CONFIG.REAL_TIME_MODE_ERROR')
      );
      return;
    }

    try {
      this.currentStateService.setShowLoading(true);
      const deviceConfig: DeviceConfig = {
        ...formValue,
        trgSnd: parseFloat((20e-6 * Math.pow(10, formValue.trgSnd / 20)).toFixed(6)),
        tmrStartTime: this.formatTime(formValue.tmrStartTime),
        tmrEndTime: this.formatTime(formValue.tmrEndTime),
        deviceSerial: this.deviceSerialNumber,
        siteId: this.siteId,
        langue: this.translate.currentLang ?? 'en'
      };      
      const response = await this.deviceConfigService.updateDeviceConfiguration(
        deviceConfig
      ).toPromise();

      if (response?.success) {
        this.fonctionsService.launch_toast(true, this.translate.instant('DEVICE_CONFIG.SAVE_SUCCESS'));
      } else {
        throw new Error(response?.message || 'Update failed');
      }
    } catch (error) {
      console.error('Error updating device configuration:', error);
      this.fonctionsService.launch_toast(false, this.translate.instant('DEVICE_CONFIG.SAVE_ERROR'));
    } finally {
      this.currentStateService.setShowLoading(false);
    }
  }

  getWorkingStatusOptions() {
    return [
      { value: WorkingStatusEnum.NO_REC, label: 'DEVICE_CONFIG.NO_RECORDING' },
      { value: WorkingStatusEnum.WAVE, label: 'DEVICE_CONFIG.WAVE_MODE' },
      { value: WorkingStatusEnum.BAR, label: 'DEVICE_CONFIG.BAR_MODE' },
      { value: WorkingStatusEnum.BOTH, label: 'DEVICE_CONFIG.BOTH_MODE' }
    ];
  }

get receivedDate() {
  return this.formGroup.get('receivedDate')?.value;
}

  private formatTime(timeString: string): string {
    if (!timeString) return '00:00:00';
    if (/^\d{2}:\d{2}:\d{2}$/.test(timeString)) return timeString;
    if (/^\d{2}:\d{2}$/.test(timeString)) return timeString + ':00';
    const d = new Date(`1970-01-01T${timeString}`);
    if (!isNaN(d.getTime())) {
      return d.toTimeString().substring(0, 8);
    }
    return '00:00:00';
  }
}
